import { useEffect, useState } from 'react';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { Stack, Box } from '@mui/material';
import { Button } from 'system/atoms';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import {
  useOnCreateCandidateBatchSubscription,
  useSearchCandidatesBatchesQuery
} from 'generated-graphql/hooks/apollo-hooks';
import {
  SearchableCandidateBatchSortableFields,
  SearchableSortDirection
} from 'generated-graphql/types';
import { TableV2 } from 'components/Table/TableV2';
import { useDebounce } from '@uidotdev/usehooks';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { getChooseAnalysisTableColumns } from 'modules/campaign-creation/constants/chooseAnalysisTableColumns';
import { extractListItems } from 'core/utils/apollo/extractListItems';
import { useUser } from 'core/stores/hooks/useUser';
import { ChooseAnalysisItem } from 'modules/campaign-creation/types/ChooseAnalysisItem';
import { SearchInput } from 'common/ui';
import { ToastType, useToast } from 'services/toast';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getSearchFilter } from 'modules/campaign-creation/utils/getSearchFilter';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

type CampaignCreationChooseAnalysisProps = {
  onSelectAnalysis: (analysisId: string) => void;
  onBack: () => void;
  onSkip: () => void;
};

export const CampaignCreationChooseAnalysis: React.FC<
  CampaignCreationChooseAnalysisProps
> = ({ onSelectAnalysis, onBack, onSkip }) => {
  const organization = useOrganization();
  const user = useUser();
  const { showToast } = useToast();
  const [searchText, setSearchText] = useState('');
  const [showOnlyMyAnalyses, setShowOnlyMyAnalyses] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] =
    useState<ChooseAnalysisItem | null>(null);
  const [limit, setLimit] = useState(24);
  const [page, setPage] = useState(0);
  // Flag defaults on; only an explicit `false` from PostHog hides the button
  const isSkipEnabled = useFlag(Flags.CAMPAIGN_ANALYSIS_SKIP_BUTTON) !== false;

  const debouncedSearchText = useDebounce(searchText, 500);

  // Reset to first page when filters or search text change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchText, showOnlyMyAnalyses]);

  const filter = getSearchFilter({
    organizationId: organization.id,
    userId: user.id,
    debouncedSearchText,
    showOnlyMyAnalyses
  });

  const {
    loading,
    data,
    refetch: refetchAnalyses
  } = useSearchCandidatesBatchesQuery({
    variables: {
      filter,
      sort: {
        field: SearchableCandidateBatchSortableFields.UpdatedAt,
        direction: SearchableSortDirection.Desc
      },
      limit,
      from: page * limit
    }
  });

  useOnCreateCandidateBatchSubscription({
    onData: () => {
      refetchAnalyses();
    }
  });

  const analyses = extractListItems(data?.searchCandidateBatches?.items || []);
  const total = data?.searchCandidateBatches?.total || 0;

  const handleAnalysisSelect = (analysis: ChooseAnalysisItem) => {
    setSelectedAnalysis(analysis);
  };

  const handleNext = () => {
    if (!selectedAnalysis) {
      showToast({
        title: 'Choose an analysis',
        type: ToastType.Error
      });
      return;
    }

    onSelectAnalysis(selectedAnalysis.id);
  };

  const columns = getChooseAnalysisTableColumns();

  return (
    <>
      <Header
        currentBreadcrumb="Choose analysis"
        title="Choose analysis"
        subtitle="Select an analysis to link this campaign to"
        breadcrumbs={[
          {
            name: 'Campaigns',
            path: Paths.CAMPAIGNS
          }
        ]}
        onBack={onBack}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              variant="tertiary"
              onClick={() => {
                refetchAnalyses();
              }}
            >
              <RefreshIcon />
              Refresh
            </Button>
            {isSkipEnabled && (
              <Button variant="secondary" onClick={onSkip}>
                Do this later
              </Button>
            )}
            <Button variant="primary" onClick={handleNext}>
              Confirm and continue
            </Button>
          </Stack>
        }
      />

      <Box sx={{ mt: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <SearchInput
            defaultValue={searchText}
            onChange={(e) => setSearchText(e)}
            placeholder="Search jobs"
            sx={{ width: 320 }}
          />
          <Checkbox
            label="Only show my analyses"
            checked={showOnlyMyAnalyses}
            onChange={(e) => setShowOnlyMyAnalyses(e.target.checked)}
          />
        </Stack>

        <TableV2
          columns={columns}
          data={analyses}
          isLoading={loading}
          pagination
          paginationServer
          paginationTotalRows={total}
          onChangeRowsPerPage={(rowsPerPage) => {
            setLimit(rowsPerPage);
            setPage(0);
          }}
          onChangePage={(page) => {
            setPage(page - 1);
          }}
          selectableRows
          selectableRowsSingle
          serverPage={page + 1}
          paginationPerPage={limit}
          onSelectedRowsChange={(params) => {
            if (params.selectedRows.length > 0) {
              const selectedRow = analyses.find(
                (row) => row.id === params.selectedRows[0].id
              );
              if (selectedRow) {
                handleAnalysisSelect(selectedRow);
              }
            } else {
              setSelectedAnalysis(null);
            }
          }}
        />
      </Box>
    </>
  );
};
